import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import Editor from './components/Editor';
import Auth from './components/Auth';
import TemplateShare from './components/TemplateShare';
import { Toaster } from './components/ui/sonner';
import { supabase } from './supabaseClient';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ProjectsProvider } from './contexts/ProjectsContext';
import { TemplatesProvider } from './contexts/TemplatesContext';
import { initAnalytics, identifyUser } from './services/analyticsService';

// Main App Component that handles auth and routing logic
const MainApp = () => {
  const [user, setUser] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Initialize analytics on app load
    initAnalytics();

    if (supabase) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        if (currentUser) {
          identifyUser(currentUser.id, { email: currentUser.email });
        }
        setLoading(false);
      });

      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        if (currentUser) {
          identifyUser(currentUser.id, { email: currentUser.email });
        }
        setLoading(false);
      });

      return () => subscription.unsubscribe();
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  // Check for redirect param after login
  if (user) {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (redirect) {
      return <Navigate to={decodeURIComponent(redirect)} replace />;
    }
  }

  if (!user) {
    return (
      <>
        <Auth supabase={supabase} onLogin={setUser} />
        <Toaster />
      </>
    );
  }

  return (
    <SubscriptionProvider>
      <ProjectsProvider>
        <TemplatesProvider>
          {currentProject ? (
            <Editor
              user={user}
              supabase={supabase}
              project={currentProject}
              onBack={() => setCurrentProject(null)}
              onLogout={() => supabase.auth.signOut()}
            />
          ) : (
            <Dashboard
              user={user}
              supabase={supabase}
              onSelectProject={setCurrentProject}
              onLogout={() => supabase.auth.signOut()}
            />
          )}
          <Toaster position="top-right" />
        </TemplatesProvider>
      </ProjectsProvider>
    </SubscriptionProvider>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/template/:templateId" element={<TemplateShare />} />
        <Route path="/login" element={<MainApp />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
